import React from 'react';
import { Avatar, Tooltip } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-light-svg-icons';
import {
  constructAvatarInitials,
  useProfilePhoto,
} from '../../../components/notes/AvatarWithInitialsFallBack';
import { Flexbox } from '../../../components/layout/Flexbox';
import { useUpdateCmailAssignment } from '../hooks/useUpdateCmailAssignment';
import { AssignUserModal } from './AssignUserModal';
import { UserSearchResult } from '../hooks/useSearchUsers';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import {
  showsStaffAvatar,
  UserAvatar,
} from '../../../components/user-avatar/UserAvatar';
import { useStaffAvatarManifest } from '../../../components/user-avatar/StaffAvatarManifest';
import classes from './AssignedToCell.module.css';

type AssignedToCellProps = {
  msgId: string;
  assignedTo?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
};

export const AssignedToCell = ({ msgId, assignedTo }: AssignedToCellProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [inputValue, setInputValue] = React.useState('');
  const { data: currentUser } = useMeQuery();
  const clickTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const { data: profilePhoto } = useProfilePhoto({
    email: assignedTo?.email,
  });

  const updateAssignment = useUpdateCmailAssignment();
  const isCowbellPersona = useStaffAvatarManifest() !== undefined;

  React.useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  const displayName = assignedTo
    ? `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`.trim() ||
      assignedTo.email
    : 'Unassigned';

  const initials = assignedTo
    ? constructAvatarInitials(
        `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`
      )
    : null;

  const handleAvatarClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    clickTimerRef.current = setTimeout(() => {
      setIsModalOpen(true);
      setSearchTerm('');
      setInputValue('');
    }, 250);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchTerm('');
    setInputValue('');
  };

  const handleUserSelect = (value: string | UserSearchResult | null) => {
    if (value && typeof value === 'object') {
      updateAssignment.mutate({
        msgId,
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
      });
      handleCloseModal();
    }
  };

  const handleAssignToMe = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    if (currentUser) {
      updateAssignment.mutate({
        msgId,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      });
    }
  };

  return (
    <>
      <Flexbox width="3rem" minWidth="3rem" justifyContent="center">
        <Tooltip
          label={
            assignedTo
              ? `Assigned to: ${displayName} (${assignedTo.email}). Click to change. Double-click to assign to yourself.`
              : 'Click to assign. Double-click to assign to yourself.'
          }
          withArrow
        >
          {showsStaffAvatar(isCowbellPersona, assignedTo?.email) ? (
            <button
              type="button"
              aria-label={`Assigned to ${displayName}`}
              onClick={handleAvatarClick}
              onDoubleClick={handleAssignToMe}
              className={`${classes.avatar} ${classes.avatarButton}`}
            >
              <UserAvatar
                email={assignedTo?.email}
                name={displayName}
                size="md"
                legacySrc={profilePhoto}
              />
            </button>
          ) : (
            <Avatar
              src={profilePhoto}
              alt={initials || 'Unassigned'}
              onClick={handleAvatarClick}
              onDoubleClick={handleAssignToMe}
              size={32}
              className={classes.avatar}
              color={assignedTo ? 'blue' : 'gray'}
              style={{ fontSize: '0.875rem' }}
            >
              {initials || (
                <FontAwesomeIcon
                  icon={faUserPlus}
                  style={{ fontSize: '0.875rem' }}
                />
              )}
            </Avatar>
          )}
        </Tooltip>
      </Flexbox>

      <AssignUserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUserSelect={handleUserSelect}
        searchTerm={searchTerm}
        inputValue={inputValue}
        onSearchTermChange={setSearchTerm}
        onInputValueChange={setInputValue}
      />
    </>
  );
};
